import type { RackNode, RowConfig, RowPosition, RowPositionKind } from '../store/editorStore.ts'
import { applyMask, buildMaskVars } from './naming.ts'
import { formatNamingProfile, resolveNamingProfileVersion, type NamingProfileAssignments, type NamingProfileCatalog } from './namingProfile.logic.ts'

type FloorPlan = { originXMm: number; originYMm: number; widthMm: number; heightMm: number }
type SlotOptions = {
  row: RowConfig
  racks: RackNode[]
  position: number
  kind: Exclude<RowPositionKind, 'rack'>
  label?: string
  floorPlan: FloorPlan
  namingPattern: string
  module: string
  hall: string
  namingProfiles?: NamingProfileCatalog
  namingAssignments?: NamingProfileAssignments
}

export type RowSlotPlan = {
  canApply: boolean
  nextRow: RowConfig
  additions: RackNode[]
  errors: string[]
}

const id = () => Math.random().toString(36).slice(2, 10)

function slotList(row: RowConfig, racks: RackNode[]): RowPosition[] {
  const positions = row.positions?.map(position => ({ ...position })) ?? []
  const known = new Set(positions.map(position => position.position))
  racks.forEach(rack => {
    const position = rack.rackPosInRow
    if (position && !known.has(position)) positions.push({ id: `${row.id}-position-${position}`, position, kind: 'rack' })
  })
  return positions.sort((left, right) => left.position - right.position)
}

function rackAddress(options: SlotOptions): string {
  if (options.label?.trim()) return options.label.trim()
  const module = Number(options.module)
  if (options.namingProfiles && options.namingAssignments && Number.isInteger(module) && module > 0) {
    const profile = resolveNamingProfileVersion(options.namingProfiles, options.namingAssignments, { rowId: options.row.id, moduleId: options.row.moduleId ?? undefined })
    return formatNamingProfile(profile.profile, { module, row: options.row.labelIndex, rack: options.position })
  }
  return applyMask(options.namingPattern, buildMaskVars({ module: options.module, hall: options.hall, row: options.row.labelIndex, rack: options.position }))
}

function inside(rack: RackNode, floorPlan: FloorPlan): boolean {
  const minX = floorPlan.originXMm / 1000 * 100
  const minY = floorPlan.originYMm / 1000 * 100
  const maxX = minX + floorPlan.widthMm / 1000 * 100
  const maxY = minY + floorPlan.heightMm / 1000 * 100
  return rack.x >= minX && rack.y >= minY && rack.x + rack.width <= maxX && rack.y + rack.height <= maxY
}

export function previewRowSlot(options: SlotOptions): RowSlotPlan {
  const errors: string[] = []
  if (!Number.isInteger(options.position) || options.position < 1) errors.push('Позиция должна быть положительным целым числом')
  const positions = slotList(options.row, options.racks)
  const existing = positions.find(position => position.position === options.position)
  const currentRack = options.racks.find(rack => rack.rackPosInRow === options.position)
  if (currentRack || (existing && existing.kind && existing.kind !== 'empty')) errors.push(`Позиция ${options.position} уже занята`)
  if (errors.length) return { canApply: false, nextRow: options.row, additions: [], errors }

  const nextPosition: RowPosition = {
    id: existing?.id ?? `${options.row.id}-position-${options.position}`,
    position: options.position,
    kind: options.kind,
    label: options.kind === 'reservation' ? options.label?.trim() || undefined : undefined,
  }
  const nextPositions = [...positions.filter(position => position.position !== options.position), nextPosition]
    .sort((left, right) => left.position - right.position)
  const nextRow: RowConfig = { ...options.row, positions: nextPositions }
  const additions: RackNode[] = []

  if (options.kind === 'inrow_cooler') {
    const reference = [...options.racks].sort((left, right) => (left.rackPosInRow ?? 0) - (right.rackPosInRow ?? 0))[0]
    if (!reference) {
      errors.push('Для In-Row Cooler нужен хотя бы один ориентирующий объект в ряду')
    } else {
      const step = (reference.widthM ?? reference.width / 100) * 100
      const sequenceSign = options.row.sequenceDirection === 'reverse' ? -1 : 1
      const axis = options.row.axis ?? (options.row.orientation === 90 || options.row.orientation === 270 ? 'vertical' : 'horizontal')
      const distance = (options.position - (reference.rackPosInRow ?? 1)) * step * sequenceSign
      const x = reference.x + (axis === 'horizontal' ? distance : 0)
      const y = reference.y + (axis === 'vertical' ? distance : 0)
      const cooler: RackNode = {
        ...reference,
        id: id(), x, y, label: rackAddress(options), rackType: 'inrow_cooler',
        isFacility: true, isBlocked: false, uCount: 0, status: 'planned',
        rackPosInRow: options.position, rackIndex: options.position - 1,
        rowIndex: options.row.labelIndex - 1, isDirty: true, dbId: undefined,
      }
      if (!inside(cooler, options.floorPlan)) errors.push(`Позиция ${options.position} выходит за границы плана`)
      additions.push(cooler)
    }
  }

  return { canApply: errors.length === 0, nextRow, additions, errors }
}
